import { useMemo } from 'react';

import { LOCATION_STATUS } from '@hetivo/hetivo-lib-types';
import type { MultiSelectOption } from '@hetivo/hetivo-lib-web-utils';

import { useLocations } from './use-locations';

export interface UseLocationsForSelectOptions {
  enabled?: boolean;
  excludeIds?: string[];
  ids?: string[];
  territoryIds?: string[];
}

export interface LocationsForSelect {
  /**
   * True once the request answered in full (or there was nothing to ask), so a caller can tell "none" from "not yet".
   * An answer that reports more locations than it returned (`total` beyond the page) is not full: a caller may not
   * conclude that a location is missing from it.
   */
  isLoaded: boolean;
  options: MultiSelectOption[];
}

/**
 * The active locations as select options, with whether they have loaded (SWOOP-401: the inspections Location filter
 * clears a choice that is not offered, which it may only do once the options are known).
 */
export function useLocationsForSelectQuery(options?: UseLocationsForSelectOptions): LocationsForSelect {
  const { enabled = true, excludeIds, ids, territoryIds } = options ?? {};

  // An explicit but empty allowlist means no location is selectable. Skip the query, since an
  // empty `ids` would otherwise be treated as "no filter" by the API and fetch every location.
  const hasEmptyAllowlist = ids !== undefined && ids.length === 0;

  const { data, isSuccess } = useLocations({
    enabled: enabled && !hasEmptyAllowlist,
    excludeIds,
    ids,
    page: 1,
    pageSize: 100,
    territoryIds,
  });

  const selectOptions = useMemo(
    () =>
      hasEmptyAllowlist
        ? []
        : (data?.items ?? [])
            .filter((location) => location.status === LOCATION_STATUS.ACTIVE)
            .map((location) => ({ label: location.name, value: location.id })),
    [data, hasEmptyAllowlist],
  );

  const isComplete = isSuccess && data.total <= data.items.length;

  return { isLoaded: hasEmptyAllowlist || isComplete, options: selectOptions };
}

export function useLocationsForSelect(options?: UseLocationsForSelectOptions): MultiSelectOption[] {
  return useLocationsForSelectQuery(options).options;
}
